import React from 'react';
import {useLocation} from 'react-router-dom';

import type {Surface} from './surface';
import {ErrorState} from './ErrorState';
import {useFragment} from './generated/fragment-masking';
import {ViewerFields_UserFragmentDoc} from './generated/graphql';
import {
  identityLoginUrl,
  redirectToIdentityLogin,
} from './identity/loginRedirect';
import {needsOnboarding} from './identity/needsOnboarding';
import {useViewer} from './identity/useViewer';
import {ShellLoader} from './ShellLoader';

/**
 * Keeps a signed-in viewer who has not finished onboarding off Forge until
 * they do. The identity landing decides between login and `/onboarding` for
 * the `next` it is given, so the handoff goes there rather than to a specific
 * route. Unlike `RequireViewer`, anonymous viewers and viewer errors pass
 * through: remotes such as wandb serve public pages without a session, and
 * `RequireViewer` handles the routes that need one.
 */
export const RequireOnboarded = ({
  children,
  origins,
  redirectToIdentityLogin: redirectToLogin = redirectToIdentityLogin,
}: {
  children: React.ReactNode;
  origins?: Partial<Record<Surface, string>>;
  redirectToIdentityLogin?: typeof redirectToIdentityLogin;
}) => {
  const location = useLocation();
  const {viewer, loading} = useViewer({skip: false});
  const viewerFields = useFragment(ViewerFields_UserFragmentDoc, viewer);
  const next = `${location.pathname}${location.search}${location.hash}`;
  const loginUrl = identityLoginUrl(origins, next);
  const redirectedRef = React.useRef(false);
  const unfinished =
    !loading && viewerFields != null && needsOnboarding(viewerFields);

  React.useEffect(() => {
    if (unfinished && loginUrl && !redirectedRef.current) {
      redirectedRef.current = true;
      redirectToLogin(origins, next);
    }
  }, [unfinished, loginUrl, next, origins, redirectToLogin]);

  if (loading) {
    return <ShellLoader label="Loading session" />;
  }

  if (unfinished) {
    return loginUrl ? null : (
      <ErrorState role="status" title="Your session couldn't be loaded." />
    );
  }

  return <>{children}</>;
};
